import { useNavigate } from 'react-router-dom';
import { Button, Table } from '@dreamtree-org/twreact-ui';
import { IndianRupee, ShoppingBag, Package, Clock, Plus, Boxes, TrendingUp, Tag } from 'lucide-react';
import { PageHeader, StatCard, StatusBadge, SectionCard, QueryState, EmptyState, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatINR, formatDate } from '@/lib/format';
import { useAuth, useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import { useCrudCount, useCrudList } from '../hooks';
import { TrendBars, StatusMix } from '../components/charts';
import { modelName, ageInDays } from '../lib/catalog';
import { compactINR } from '../lib/money';
import { hasNoOrders } from '../lib/dashboard';
import SetupChecklist from '../components/SetupChecklist';

const AGED_DAYS = 45;

/** SELL-01 — business dashboard. Sales numbers need sales:view; stock numbers come from inventory. */
export default function MerchantDashboard({ store }) {
  const nav = useNavigate();
  const user = useAuth();
  const can = useCan();
  const canSales = can('sales', 'view');
  const canInv = can('inventory', 'view');
  const canStore = can('store', 'view');
  const sales = useQuery('merchantSalesSummary', {}, { select: (r) => r.data, enabled: canSales });
  const inStock = useCrudCount('InventoryUnit', { stock_state: 'in_stock' }, { enabled: canInv });
  const live = useCrudCount('Listing', { status: 'published' }, { enabled: canInv });
  const drafts = useCrudCount('Listing', { status: 'draft' }, { enabled: canInv });
  const oldest = useCrudList('InventoryUnit', {
    where: { stock_state: 'in_stock' },
    with: ['ProductModel'],
    orderBy: { column: 'created_at', direction: 'asc' },
    limit: 5,
  }, { enabled: canInv });
  const inspections = useQuery('merchantInspections', {}, { select: (r) => r.data, enabled: canInv });
  const stockValue = useQuery('merchantStockValue', {}, { select: (r) => r.data, enabled: canInv });
  const s = sales.data;
  // A store with no orders yet sees what to set up instead of empty charts (MERCH-58).
  const noOrders = hasNoOrders(s);
  const checklist = useQuery('merchantSetupChecklist', {}, { select: (r) => r.data, enabled: canStore && noOrders });
  const sv = stockValue.data;
  const pending = canSales ? '…' : '—';
  const count = (q) => (!canInv || q.error ? '—' : q.loading && q.data === undefined ? '…' : q.data);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome${user && user.name ? `, ${user.name.split(' ')[0]}` : ''}`}
        subtitle={`${(store && store.name) || 'Your store'} · business overview`}
        actions={
          <Can module="inventory" action="create">
            <Button leftIcon={<Plus size={16} />} onClick={() => nav('/add')}>Add listing</Button>
          </Can>
        }
      />

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Sales this month"
          icon={<IndianRupee size={18} />}
          value={s ? formatINR(s.sales_this_month_paise) : pending}
          trend={s && s.sales_change_pct != null ? `${s.sales_change_pct >= 0 ? '+' : ''}${s.sales_change_pct}%` : undefined}
          hint="vs last month"
        />
        <StatCard label="Orders this month" icon={<ShoppingBag size={18} />} value={s ? s.orders_this_month : pending} hint="paid, excl. cancelled" />
        <StatCard
          label="Units in stock"
          icon={<Package size={18} />}
          value={count(inStock)}
          hint={`${count(live)} live · ${count(drafts)} draft listings`}
        />
        <StatCard
          label="Pending orders"
          icon={<Clock size={18} />}
          value={s ? s.pending_orders : pending}
          hint={s && s.overdue_acceptance ? `${s.overdue_acceptance} past acceptance cutoff` : 'new, accepted or packed'}
        />
      </div>


      {canInv && sv && sv.units > 0 ? (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-3" data-testid="stock-value">
          <StatCard label="Stock value" icon={<Boxes size={18} />} value={formatINR(sv.value_paise)} hint={`${sv.listed_units} listed unit${sv.listed_units === 1 ? '' : 's'} at listing price`} />
          {sv.margin_paise != null ? (
            <StatCard label="Potential margin" icon={<TrendingUp size={18} />} value={formatINR(sv.margin_paise)} hint={`over cost ${formatINR(sv.cost_paise)}`} />
          ) : null}
          {sv.unlisted_units ? (
            <StatCard label="Not listed yet" icon={<Tag size={18} />} value={sv.unlisted_units} hint="in stock, no listing price" />
          ) : null}
        </div>
      ) : null}

      {canSales ? (
      <>
      {noOrders ? (
        <SectionCard title="Get your store ready">
          {canStore ? (
            <QueryState query={checklist}>{(d) => <SetupChecklist data={d} />}</QueryState>
          ) : (
            <EmptyState title="No orders yet">Sales and order charts appear here once your first order is paid.</EmptyState>
          )}
        </SectionCard>
      ) : (
      <div className="grid gap-4 lg:grid-cols-3">
        <SectionCard title="Sales trend · last 6 months" className="lg:col-span-2">
          <QueryState query={sales}>
            {(d) => <TrendBars title="Monthly sales" data={d.trend.map((t) => ({ label: t.month, value: t.sales_paise }))} format={compactINR} />}
          </QueryState>
        </SectionCard>
        <SectionCard title="Order status">
          <QueryState query={sales}>
            {(d) => <StatusMix title="Orders by status" data={d.status_breakdown} />}
          </QueryState>
        </SectionCard>
      </div>
      )}

      <SectionCard title="Recent orders" actions={<Button size="sm" variant="ghost" onClick={() => nav('/orders')}>View all</Button>}>
        <QueryState query={sales} skeleton="table">
          {(d) => (!d.recent_orders || !d.recent_orders.length ? (
            <EmptyState title="No orders yet">Paid orders show up here, newest first. Share your storefront link to get the first one.</EmptyState>
          ) : (
            <Table
              data={d.recent_orders}
              columns={[
                { key: 'id', label: 'Order', render: (r) => <span className="font-medium">#{r.id}</span> },
                { key: 'placed_at', label: 'Date', render: (r) => formatDate(r.placed_at), hideOnMobile: true },
                { key: 'customer', label: 'Customer' },
                { key: 'item', label: 'Product', hideOnMobile: true },
                { key: 'amount_paise', label: 'Amount', render: (r) => <Money paise={r.amount_paise} /> },
                { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              ]}
              onRowClick={(r) => nav(`/orders/${r.id}`)}
              showSerial={false}
              withAction={false}
              showToolbar={false}
              stripedRows={false}
            />
          ))}
        </QueryState>
      </SectionCard>
      </>
      ) : null}

      {canInv ? (
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Pending inspections">
          <QueryState query={inspections}>
            {(rows) => {
              const open = (rows || []).filter((r) => r.status !== 'completed');
              if (!open.length) return <EmptyState title="No pending inspections">Every received unit has a completed checklist.</EmptyState>;
              return (
                <ul className="divide-y divide-[var(--app-border)]">
                  {open.map((r) => (
                    <li key={r.id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.title}</div>
                        <div className="text-xs opacity-70">{r.sku} · {r.reinspection ? 'returned' : 'received'} {formatDate(r.received_at)}{r.reinspection ? ' · re-inspect before relisting' : ''}{r.technician ? ` · ${r.technician}` : ''}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <StatusBadge status={r.status} />
                        <div className="text-xs opacity-70 mt-1">due {formatDate(r.due_at)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              );
            }}
          </QueryState>
        </SectionCard>

        <SectionCard title="Aged stock · oldest in-stock units" actions={<Button size="sm" variant="ghost" onClick={() => nav('/products?tab=stock')}>In stock</Button>}>
          <QueryState query={oldest}>
            {(rows) =>
              rows && rows.length ? (
                <ul className="divide-y divide-[var(--app-border)]">
                  {rows.map((u) => {
                    const age = ageInDays(u.created_at);
                    return (
                      <li key={u.id} className="py-2 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{modelName(u.ProductModel)}</div>
                          <div className="text-xs opacity-70">{u.internal_sku} · Grade {u.grade} · cost {formatINR(u.acquisition_cost_paise)}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <StatusBadge status={age >= AGED_DAYS ? 'overdue' : 'in_stock'} label={`${age} day${age === 1 ? '' : 's'}`} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <EmptyState title="No units in stock">Add a device to start selling.</EmptyState>
              )
            }
          </QueryState>
          <p className="text-xs opacity-60">Units in stock for {AGED_DAYS}+ days are flagged.</p>
        </SectionCard>
      </div>
      ) : null}
    </div>
  );
}
